import { useCallback, useRef } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { ArticleFormData } from '@/types/article';

interface UseArticleAutoSaveProps {
  getValues: UseFormGetValues<ArticleFormData>;
  articleId?: string;
  enabled?: boolean;
}

const AUTO_SAVE_KEY = 'article_draft_';

export const useArticleAutoSave = ({
  getValues,
  articleId,
  enabled = true
}: UseArticleAutoSaveProps) => {
  const lastSaveRef = useRef<string>('');

  const saveDraft = useCallback(() => {
    if (!enabled) return false;

    try {
      const formData = getValues();
      const dataToSave = JSON.stringify(formData);

      // Only save if data has changed
      if (dataToSave === lastSaveRef.current) {
        return false;
      }

      const key = `${AUTO_SAVE_KEY}${articleId || 'new'}`;
      sessionStorage.setItem(key, dataToSave);
      sessionStorage.setItem(`${key}_timestamp`, new Date().toISOString());
      
      lastSaveRef.current = dataToSave;
      return true;
    } catch (error) {
      console.error('Failed to auto-save draft:', error);
      return false;
    }
  }, [getValues, articleId, enabled]);

  const loadDraft = useCallback((): Partial<ArticleFormData> | null => {
    try {
      const key = `${AUTO_SAVE_KEY}${articleId || 'new'}`;
      const savedData = sessionStorage.getItem(key);
      const timestamp = sessionStorage.getItem(`${key}_timestamp`);

      if (savedData && timestamp) {
        const savedDate = new Date(timestamp);
        const now = new Date();
        const hoursSinceLastSave = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60);

        // Only restore if saved within last 24 hours
        if (hoursSinceLastSave < 24) {
          return JSON.parse(savedData);
        }
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
    return null;
  }, [articleId]);

  const clearDraft = useCallback(() => {
    try {
      const key = `${AUTO_SAVE_KEY}${articleId || 'new'}`;
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}_timestamp`);
      lastSaveRef.current = '';
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }, [articleId]);


  return {
    saveDraft,
    loadDraft,
    clearDraft
  };
};
